// SETTE design system color/format tokens for Analytics (CLIPPING-598), ported from
// analytics-design/src/theme/{chart,semantics,format}.ts. Chart.js stays the charting library
// here (no recharts migration) - these constants just feed Chart.js `backgroundColor`/
// `borderColor`/tooltip options instead of the old ad-hoc hex values per screen.

// chart-1..8, in order. chart-1 is always reserved for the client's own brand color when a
// chart compares the client against competitors/media.
export const CHART_COLORS = ['#244896', '#2A9283', '#C9741A', '#7B5CC4', '#CC4B3F', '#3B88D2', '#7E8C22', '#7D8796'] as const;

export const MEDIA_COLORS = {
	print: '#244896',
	portal: '#2A9283',
	radio: '#C9741A',
	tv: '#7B5CC4',
	// Not part of the design's own 4-medium palette (print/portal/radio/tv only) - Social has
	// no token there, so this is the neutral chart-8 fallback, same choice made in chart.ts's
	// own Media Dashboard conversion notes.
	social: '#7D8796',
} as const;

// Light tint backgrounds for a media-type icon square.
export const MEDIA_TINT = {
	print: '#e9eef9',
	portal: '#e3f3f0',
	radio: '#fbf0e3',
	tv: '#efebf9',
	social: '#EEF0F3',
} as const;

export const SENTIMENT_COLORS = {
	positive: '#17734F',
	neutral: '#c4c9d3',
	negative: '#B4321F',
} as const;

// Multi-level sentiment scale, most-positive -> most-negative. Blue is never used for
// sentiment - it is reserved for action/selection.
export const SENTIMENT_SCALE = ['#17734F', '#6FB396', '#c4c9d3', '#E0907F', '#B4321F'] as const;
export const SENTIMENT_SCALE_LABELS = ['++', '+', '○', '−', '−−'] as const;

// Default cut-off between "strong" and "plain" tone, on a -100..100 metric (Impact).
export const SENTIMENT_STRONG_AT = 40;

// Buckets a signed tone value into a SENTIMENT_SCALE index. Symmetric around zero; zero is the
// ONLY neutral value. Non-integer inputs (averages) are rounded first, so |x| < 0.5 -> neutral.
export function sentimentScaleIndex(value: number, strongAt: number = SENTIMENT_STRONG_AT): number {
	const v = Math.round(value);
	if (v >= strongAt) return 0;
	if (v > 0) return 1;
	if (v === 0) return 2;
	if (v > -strongAt) return 3;
	return 4;
}

export function sentimentScaleColor(value: number, strongAt: number = SENTIMENT_STRONG_AT): string {
	return SENTIMENT_SCALE[sentimentScaleIndex(value, strongAt)];
}

export function sentimentScaleLabel(value: number, strongAt: number = SENTIMENT_STRONG_AT): string {
	return SENTIMENT_SCALE_LABELS[sentimentScaleIndex(value, strongAt)];
}

// Per-clip sentiment is +1 / 0 / -1, so "strong" has no meaning: every non-zero value is
// simply positive or negative.
export function clipSentimentIndex(value: number): number {
	return sentimentScaleIndex(value, Infinity);
}

// seq-1..5, light to dark - single-metric intensity/heatmap-style scales.
export const SEQ_COLORS = ['#e9eef9', '#b9c9ea', '#7d99d3', '#4a6fb8', '#244896'] as const;

export interface BrandTagLike {
	tagId?: string | null;
	brandKind?: string | null;
}

// chart-1 is reserved for the company brand (see brandColor below); chart-5 (#CC4B3F) is excluded
// from the competitor pool since several Analytics screens also show sentiment/Impact on the same
// view (same reasoning as categoricalColors' avoidSentimentClash).
const BRAND_COMPETITOR_PALETTE = CHART_COLORS.slice(1).filter((c) => c !== '#CC4B3F');

// Задача 8: one shared brand color for all 7 Analytics screens and the PDF report, replacing each
// screen's own `b.colorCode || PALETTE[i % PALETTE.length]` - "i" was the backend response's row
// order, which differs by screen and by which brands are currently selected, so the same brand
// could show up in a different color on every reload. The client's own brand (BrandKind.COMPANY)
// always gets chart-1, the main SETTE color. Competitors are ranked by a stable ascending sort of
// tagId across ALL the client's configured brand tags (brandOptions - pass the panel's full
// rawBrandTags list, not the chart's own filtered rows), so a competitor keeps its color
// regardless of which subset is currently selected or what order this particular response
// returned its rows in - Bank X is the same color today and tomorrow. Mirrored server-side in
// ReportChartHtmlBuilder.brandColor (Java) for the scheduled PDF report; colorCode (operator-set
// per tag for the tag/clip UI) is intentionally ignored here - different audience, no stability
// guarantee.
export function brandColor(tagId: string | null | undefined, brandOptions: readonly BrandTagLike[]): string {
	if (!tagId) return AXIS.text;
	const isCompany = brandOptions.some((b) => b.tagId === tagId && b.brandKind === 'COMPANY');
	if (isCompany) return CHART_COLORS[0];
	const competitorIds = brandOptions
		.filter((b) => b.brandKind !== 'COMPANY' && b.tagId)
		.map((b) => b.tagId as string)
		.sort();
	const idx = competitorIds.indexOf(tagId);
	if (idx === -1) return BRAND_COMPETITOR_PALETTE[0];
	return BRAND_COMPETITOR_PALETTE[idx % BRAND_COMPETITOR_PALETTE.length];
}

export const AXIS = {
	text: '#5f6470',
	grid: '#eef0f3',
	baseline: '#d3d8e2',
	tooltipBg: '#1c1d21',
} as const;

// chart-5 (#CC4B3F) sits close to --color-negative (#B4321F): on a screen that also shows
// sentiment/Impact, skip it so a category never reads as "negative" by color alone.
export function categoricalColors(count: number, opts: { avoidSentimentClash?: boolean } = {}): string[] {
	const pool = opts.avoidSentimentClash ? CHART_COLORS.filter((c) => c !== '#CC4B3F') : [...CHART_COLORS];
	return Array.from({ length: count }, (_, i) => pool[i % pool.length]);
}

// Text color class for a cell filled with SEQ_COLORS[level]. seq-1..3 are light enough for
// ink text; seq-4..5 need the on-blue (white) treatment.
export function seqTextColor(level: number): string {
	return level >= 3 ? 'text-on-blue' : 'text-ink';
}

// --- semantics.ts: metric + delta sign -> semantic tone ---

export type Tone = 'positive' | 'negative' | 'neutral';

// Metrics where an increase (delta > 0) is good news.
const UP_IS_GOOD = ['clips', 'mentions', 'reach', 'visibility', 'impact', 'sentimentPositive', 'shareOfVoice', 'sov'];

// Metrics where an increase (delta > 0) is bad news.
const UP_IS_BAD = ['sentimentNegative', 'negativeClips', 'negativeMentions'];

// Sign-only tone for an already-signed absolute value (e.g. a single story's Impact score,
// not a delta): positive > 0, negative < 0, and 0 is a legitimate neutral value - never
// "positive by default" (the bug Фаза 6 of SETTE_DESIGN.md fixed: `value >= 0 ? positive : …`
// counted a genuinely neutral 0 as positive).
export function signTone(value: number): Tone {
	if (value > 0) return 'positive';
	if (value < 0) return 'negative';
	return 'neutral';
}

export function deltaTone(metricKey: string, delta: number): Tone {
	if (delta === 0) return 'neutral';
	if (UP_IS_GOOD.includes(metricKey)) return delta > 0 ? 'positive' : 'negative';
	if (UP_IS_BAD.includes(metricKey)) return delta > 0 ? 'negative' : 'positive';
	// eslint-disable-next-line no-console
	console.warn(`[deltaTone] unmapped metric "${metricKey}" - defaulting to neutral. Add it to UP_IS_GOOD/UP_IS_BAD in chart-theme.ts.`);
	return 'neutral';
}

// --- format.ts: the minus sign is always U+2212 (−), never ASCII hyphen ---

const MINUS = '−';

/** Replaces every ASCII hyphen used as a minus with U+2212, in place. */
export function toMinusSign(text: string): string {
	return text.replace(/-(?=\d)/g, MINUS);
}

// Задача 7: one shared locale-number formatter for every Analytics screen (KPI tiles, chart
// axes/tooltips, tables, the "Врз основа на N" caption) - mk/al use a decimal comma and a
// thousands dot (12,5 / 1.250), en uses the reverse (12.5 / 1,250). `lang` is each component's
// own UtilsService.currentLanguage, re-read on every call (these all sit behind template-bound
// getters) so a language switch reformats immediately, no reload. ReportGenerationServiceImpl.java
// mirrors this same two-locale rule server-side for the scheduled PDF report.
function localizeDigits(absFixed: string, lang?: string): string {
	const decimalSep = lang === 'en' ? '.' : ',';
	const thousandSep = lang === 'en' ? ',' : '.';
	const [intPart, fracPart] = absFixed.split('.');
	const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandSep);
	return fracPart !== undefined ? `${grouped}${decimalSep}${fracPart}` : grouped;
}

/** Unsigned number, `decimals` fixed digits (1 for averages/percentages, 0 for mention counts),
 *  locale-correct separators. '—' for null/undefined/non-finite, same placeholder every KPI tile
 *  already shows for missing data. */
export function formatAnalyticsNumber(value: number | null | undefined, lang?: string, decimals = 1): string {
	if (value == null || !Number.isFinite(value)) return '—';
	return localizeDigits(Math.abs(value).toFixed(decimals), lang);
}

/** Whole-number formatting (mention counts) - same thousands grouping, no decimals. */
export function formatAnalyticsInt(value: number | null | undefined, lang?: string): string {
	return formatAnalyticsNumber(value, lang, 0);
}

/** Mirrors plain `value.toFixed(decimals)` but with locale-correct separators: keeps the native
 *  minus for a negative value, adds no explicit '+' for a positive one (unlike formatSigned). Use
 *  wherever the original code called `.toFixed()` directly on a value that can go negative but was
 *  shown with no explicit sign convention (e.g. a "leads by X pp" gap that can flip negative). */
export function formatAnalyticsFixed(value: number, lang?: string, decimals = 1): string {
	if (!Number.isFinite(value)) return '—';
	return (value < 0 ? '-' : '') + formatAnalyticsNumber(value, lang, decimals);
}

const EN_MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Full date, language-dependent: "14.10.2026" (mk/al) vs "14 Oct 2026" (en). */
export function formatAnalyticsDate(date: Date, lang?: string): string {
	if (lang === 'en') return `${date.getDate()} ${EN_MONTHS_SHORT[date.getMonth()]} ${date.getFullYear()}`;
	const day = date.getDate().toString().padStart(2, '0');
	const month = (date.getMonth() + 1).toString().padStart(2, '0');
	return `${day}.${month}.${date.getFullYear()}`;
}

/** Short date, no year - for chart axis/point labels: "14.10" (mk/al) vs "14 Oct" (en). */
export function formatAnalyticsAxisDate(date: Date, lang?: string): string {
	if (lang === 'en') return `${date.getDate()} ${EN_MONTHS_SHORT[date.getMonth()]}`;
	const day = date.getDate().toString().padStart(2, '0');
	const month = (date.getMonth() + 1).toString().padStart(2, '0');
	return `${day}.${month}`;
}

/** Signed number for display: explicit + for positives, U+2212 for negatives, bare zero. */
export function formatSigned(n: number, digits = 1, lang?: string): string {
	if (!Number.isFinite(n)) return '—';
	const rounded = Number(n.toFixed(digits));
	const body = localizeDigits(Math.abs(rounded).toFixed(digits), lang);
	if (rounded > 0) return `+${body}`;
	if (rounded < 0) return `${MINUS}${body}`;
	return body;
}

/** Same as formatSigned but without forcing decimals (integers stay integers). */
export function formatSignedAuto(n: number, lang?: string): string {
	if (!Number.isFinite(n)) return '—';
	const body = localizeDigits(String(Math.abs(n)), lang);
	if (n > 0) return `+${body}`;
	if (n < 0) return `${MINUS}${body}`;
	return body;
}
